/**
 * src/pages/FallacyLibrary/data/index.ts
 * Master index merging all 54 fallacies across 7 categories.
 */

import type { FallacyEntry, FallacyCategory } from "../types";
import { RELEVANCE_FALLACIES } from "./relevance";
import { PRESUMPTION_FALLACIES } from "./presumption";
import { EVIDENCE_FALLACIES } from "./evidence";
import { CAUSAL_FALLACIES } from "./causal";
import { LANGUAGE_FALLACIES } from "./language";
import { FORMAL_FALLACIES } from "./formal";
import { STATISTICAL_FALLACIES } from "./statistical";

export {
  RELEVANCE_FALLACIES,
  PRESUMPTION_FALLACIES,
  EVIDENCE_FALLACIES,
  CAUSAL_FALLACIES,
  LANGUAGE_FALLACIES,
  FORMAL_FALLACIES,
  STATISTICAL_FALLACIES,
};

export const ALL_FALLACIES: FallacyEntry[] = [
  ...RELEVANCE_FALLACIES, // 10
  ...PRESUMPTION_FALLACIES, // 9
  ...EVIDENCE_FALLACIES, // 9
  ...CAUSAL_FALLACIES, // 6
  ...LANGUAGE_FALLACIES, // 7
  ...FORMAL_FALLACIES, // 5
  ...STATISTICAL_FALLACIES, // 10
];

export interface CategoryMeta {
  id: FallacyCategory;
  name: string;
  count: number;
  description: string;
}

export const CATEGORIES_META: CategoryMeta[] = [
  {
    id: "relevance",
    name: "Relevance & Rhetorical",
    count: RELEVANCE_FALLACIES.length,
    description:
      "Arguments where premises lack genuine logical relevance to the truth of the conclusion, substituting personal attacks, emotional manipulation, or diversions.",
  },
  {
    id: "presumption",
    name: "Presumption & Argument Structure",
    count: PRESUMPTION_FALLACIES.length,
    description:
      "Arguments that smuggle in unjustified assumptions, build circular epistemic loops, or manufacture false dilemmas to bypass the burden of proof.",
  },
  {
    id: "evidence",
    name: "Evidence & Generalization",
    count: EVIDENCE_FALLACIES.length,
    description:
      "Inductive reasoning errors resulting from unrepresentative samples, suppressed counter-evidence, survival filtering, or isolated personal anecdotes.",
  },
  {
    id: "causal",
    name: "Causal Errors",
    count: CAUSAL_FALLACIES.length,
    description:
      "Flaws in attributing causes and effects, including confusing correlation with causation, ignoring omitted confounders, or mistaking temporal succession for causality.",
  },
  {
    id: "language",
    name: "Language & Ambiguity",
    count: LANGUAGE_FALLACIES.length,
    description:
      "Fallacies exploiting shifting semantic definitions, ambiguous syntax, mereological part-whole confusion, or misleading linguistic framing.",
  },
  {
    id: "formal",
    name: "Formal Fallacies",
    count: FORMAL_FALLACIES.length,
    description:
      "Defects in the structural deductive form of propositional or categorical syllogisms, where the conclusion does not follow by logical necessity even if premises are true.",
  },
  {
    id: "statistical",
    name: "Statistical & Probabilistic Errors",
    count: STATISTICAL_FALLACIES.length,
    description:
      "Errors in probabilistic reasoning, base-rate calculations, conditional probabilities, effect size interpretations, and confidence interval estimation.",
  },
];
